import { useEffect } from 'react';

interface UseDropdownLifecycleOptions {
  isOpen: boolean;
  onClose?: () => void;
  dropdownRef?: React.RefObject<HTMLElement>;
  anchorRef?: React.RefObject<HTMLElement>;
  closeOnScroll?: boolean;
  closeOnResize?: boolean;
}

export function useDropdownLifecycle({
  isOpen,
  onClose,
  dropdownRef,
  anchorRef,
  closeOnScroll = true,
  closeOnResize = true
}: UseDropdownLifecycleOptions): void {
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef?.current && !dropdownRef.current.contains(event.target as Node)) {
        if (anchorRef?.current && !anchorRef.current.contains(event.target as Node)) {
          onClose?.();
        }
      }
    };
    
    const handleScrollOrResize = (event: Event) => {
      if (event.type === 'scroll' && event.target) {
        const target = event.target as HTMLElement;
        if (dropdownRef?.current && dropdownRef.current.contains(target)) {
          return;
        }
      }
      
      if (event.type === 'scroll' && !closeOnScroll) return;
      if (event.type === 'resize' && !closeOnResize) return;
      
      onClose?.();
    };
    
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, dropdownRef, anchorRef, closeOnScroll, closeOnResize]);
}

